import { Link } from "@tanstack/react-router";
// import type { CSSProperties } from "react";
import { useState } from "react";
// import { Carousel } from "#/components/ui/carousel";
import { ASSETS, pickFromCovers } from "#/lib/assets";
import {
  curatedCover,
  LIVING_BOOK_AUTHORS,
  LIVING_BOOK_HANDLES,
} from "#/lib/homepage-curated";
import { ROUTES } from "#/lib/routes";
import { type Product, useProducts } from "#/queries/products";
import { BookCover, PillBtn, Skeleton } from "../primitives.js";

const BOOK_TRANSITION =
  "transform 1100ms cubic-bezier(0.68, -0.3, 0.265, 1.3), opacity 1000ms ease-in-out, filter 800ms ease-out";
const TEXT_TRANSITION = BOOK_TRANSITION;

const DESKTOP_TEXT_HEIGHT = 400;
const MOBILE_TEXT_HEIGHT = 180;

interface LivingBookView {
  id: string;
  cover: string;
  title: string;
  author: string;
  description: string;
}

function toLivingBookView(p: Product): LivingBookView {
  return {
    id: p.id,
    cover:
      curatedCover(p.handle)?.src ??
      p.thumbnail ??
      p.images?.[0]?.url ??
      pickFromCovers(p.id, ASSETS.livingBooksCovers),
    title: p.title,
    author: LIVING_BOOK_AUTHORS[p.handle ?? ""] ?? p.subtitle ?? "",
    description: p.description ?? "",
  };
}

type Slot = "hiddenTop" | "top" | "main" | "bottom" | "hiddenBottom";

interface SlotBox {
  left: number;
  top: number;
  width: number;
  height: number;
}

const DESKTOP_SLOTS: Record<Slot, SlotBox> = {
  hiddenTop: { left: 700, top: -40, width: 170, height: 250 },
  top: { left: 580, top: -20, width: 170, height: 250 },
  main: { left: 160, top: 120, width: 420, height: 520 },
  bottom: { left: 580, top: 500, width: 170, height: 250 },
  hiddenBottom: { left: 700, top: 500, width: 170, height: 250 },
};

const MOBILE_SLOTS: Record<Slot, SlotBox> = {
  hiddenTop: { left: 260, top: 0, width: 110, height: 160 },
  top: { left: 190, top: 0, width: 110, height: 160 },
  main: { left: 10, top: 40, width: 200, height: 290 },
  bottom: { left: 190, top: 220, width: 110, height: 160 },
  hiddenBottom: { left: 260, top: 220, width: 110, height: 160 },
};


function getSlotForOffset(offset: number): Slot {
  if (offset === 0) return "main";
  if (offset === -1) return "top";
  if (offset === 1) return "bottom";
  if (offset <= -2) return "hiddenTop";
  return "hiddenBottom";
}

function BookStack({
  items,
  slots,
  onPrev,
  onNext,
}: {
  items: Array<{ item: LivingBookView; absoluteIndex: number; offset: number }>;
  slots: Record<Slot, SlotBox>;
  onPrev: () => void;
  onNext: () => void;
}) {
  return (
    <>
      {items.map(({ item, absoluteIndex, offset }) => {
        const isCenter = offset === 0;
        const isVisible = Math.abs(offset) <= 1;

        const slot = getSlotForOffset(offset);
        const target = slots[slot];
        const main = slots.main;

        const scale = target.width / main.width;
        const tx = target.left + target.width / 2 - main.width / 2;
        const ty = target.top + target.height / 2 - main.height / 2;
        return (
          <button
              key={`book-${absoluteIndex}`}
            type="button"
            disabled={isCenter || !isVisible}
            aria-label={isCenter ? item.title : `Focus ${item.title}`}
            onClick={() => {
              if (offset === -1) onPrev();
              if (offset === 1) onNext();
            }}
            className="absolute flex cursor-pointer items-center justify-center border-0 bg-transparent p-0 disabled:cursor-default"
            // style={slotStyle(slot, slots)}
            style={{
              left: 0,
              top: 0,
              width: main.width,
              height: main.height,
              transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
              transformOrigin: "center",
              opacity: isCenter ? 1 : isVisible ? 0.75 : 0,
              filter: isCenter ? "none" : isVisible ? "blur(4px) saturate(0.55)" : "blur(10px)",
              transition: BOOK_TRANSITION,
              zIndex: isCenter ? 2 : isVisible ? 1 : 0,
              pointerEvents: isVisible ? "auto" : "none",
            }}
          >
            <BookCover
               src={item.cover}
              w={main.width}
              h={main.height}
              alt={item.title}
              fit="contain"
              loading="lazy"
            />
          </button>
        );
      })}
    </>
  );
}

export function LivingBooks() {
  const { data } = useProducts({
    handles: LIVING_BOOK_HANDLES,
    limit: LIVING_BOOK_HANDLES.length,
  });

  const [i, setI] = useState(0);
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);

  if (data === undefined) {
    return <LivingBooksSkeleton />;
  }
  const books = data.items.map(toLivingBookView);
  const total = books.length;

  if (total === 0) return null;

  const handlePrev = () => setI((prev) => prev - 1);
  const handleNext = () => setI((prev) => prev + 1);

  const handleTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0]?.clientY ?? null);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0]?.clientY ?? null);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    
    if (distance > 50) handleNext();
    if (distance < -50) handlePrev();
  };

  const visibleOffsets = [-2, -1, 0, 1, 2];
  const visibleItems = visibleOffsets.flatMap((offset) => {
    const absoluteIndex = i + offset;
    const dataIndex = ((absoluteIndex % total) + total) % total;
    const item = books[dataIndex];
    return item ? [{ item, absoluteIndex, offset }] : [];
  });

  return (
    <section className="relative pt-16 md:pt-44 pb-16 md:pb-24 overflow-hidden">
      {/* DESKTOP VIEW */}
      <div className="hidden lg:block relative mx-auto min-h-[700px] max-w-6xl px-10">
        <div className="relative z-10 flex flex-col items-start w-full max-w-[420px] pt-16">
          
          <h2 className="m-0 font-bold font-display text-3xl sm:text-4xl md:text-5xl text-[#6F6E6E] leading-tight tracking-wide uppercase">
            LIVING BOOKS
          </h2>

          <div className="relative w-full h-[320px] my-24">
            <div className="absolute -left-[100vw] -top-10 z-0 h-[400px] w-[calc(100vw+550px)] w-[50vw] rounded-r-full bg-[#D3E9CC]" />

            <div className="absolute -top-10 left-0 z-10 h-[400px] w-full overflow-hidden">
              {visibleItems.map(({ item, absoluteIndex, offset }) => {
                const isCenter = offset === 0;
                const isVisible = Math.abs(offset) <= 1;

                return (
                  <div
                    key={`txt-desktop-${absoluteIndex}`}
                    className="absolute inset-x-0 top-0 flex flex-col justify-center h-full"
                    style={{
                      transform: `translateY(${offset * DESKTOP_TEXT_HEIGHT}px)`,
                      opacity: isCenter ? 1 : isVisible ? 0.15 : 0,
                      filter: isCenter ? "none" : "blur(2px)",
                      transition: TEXT_TRANSITION,
                      pointerEvents: isCenter ? "auto" : "none",
                    }}
                  >
                    <h3 className="m-0 mb-3 max-w-[380px] font-bold font-display text-3xl text-[#6F6E6E] leading-tight line-clamp-2">
                      {item.title}
                    </h3>
                    <div className="mb-4 font-display font-semibold text-lg text-[#6F6E6E]">
                      {item.author}
                    </div>
                    <p className="m-0 max-w-[360px] line-clamp-6 font-display text-[#6F6E6E] text-sm leading-relaxed">
                      {item.description}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
      </div>

       <div className="relative z-20 flex w-full">
            <Link to={ROUTES.livingBooks}>
            <button type="button" className="bg-[#ABD79E] text-base rounded-full text-white py-4 px-16 font-semibold hover:cursor-pointer hover:bg-[#45553F] transition-all duration-500 ease-in-out">See More Living Books</button>
            </Link>
        </div>
      </div>

      <div className="hidden lg:block absolute right-0 xl:right-[7%] 2xl:right-[12%] top-0 md:top-30 z-20 h-[700px] w-[700px]">
        <BookStack
            items={visibleItems}
            slots={DESKTOP_SLOTS}
            onPrev={handlePrev}
            onNext={handleNext}
          />
      </div>
    
      <div 
        className="lg:hidden flex flex-col items-center w-full"
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{ touchAction: "pan-y" }}
      >
        <div className="w-full max-w-5xl px-6 mb-4 flex justify-center text-center">
          <h2 className="m-0 font-bold font-display text-3xl text-[#6F6E6E] leading-tight uppercase">
            LIVING BOOKS
          </h2>
        </div>

        <div className="relative w-full h-[400px] max-w-[320px] mx-auto z-20 mt-4">
          <BookStack
            items={visibleItems}
            slots={MOBILE_SLOTS}
            onPrev={handlePrev}
            onNext={handleNext}
          />
        </div>

         <div className="w-full max-w-sm mx-auto mt-6 px-6">
          <div className="relative h-[180px] w-full overflow-hidden">
            {visibleItems.map(({ item, absoluteIndex, offset }) => {
              const isCenter = offset === 0;
              const isVisible = Math.abs(offset) <= 1;

              return (
                <div
                  key={`txt-mobile-${absoluteIndex}`}
                  className="absolute inset-x-0 top-0 flex flex-col items-center justify-center text-center h-full"
                  style={{
                    transform: `translateY(${offset * MOBILE_TEXT_HEIGHT}px)`,
                    opacity: isCenter ? 1 : isVisible ? 0.15 : 0,
                    filter: isCenter ? "none" : "blur(1px)",
                    transition: TEXT_TRANSITION,
                    pointerEvents: isCenter ? "auto" : "none",
                  }}
                >
                  <h3 className="m-0 mb-2 max-w-[280px] font-bold font-display text-2xl text-[#6F6E6E] leading-tight line-clamp-2">
                    {item.title}
                  </h3>
                  <div className="mb-2 font-display font-semibold text-base text-[#6F6E6E]">
                    {item.author}
                  </div>
                  <p className="m-0 max-w-[300px] font-display text-sm text-[#6F6E6E] leading-relaxed line-clamp-3">
                    {item.description}
                  </p>
                </div>
              );
            })}
          </div>
          <div className="flex justify-center relative z-10 mt-2">
            <Link to={ROUTES.livingBooks}>
              <PillBtn variant="green" size="sm">See More Living Books</PillBtn>
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
}

function LivingBooksSkeleton() {
  return (
    <section
      className="relative pt-24 md:pt-44 pb-16 md:pb-24 overflow-hidden"
      aria-label="Loading living books"
      role="status"
    >
      <div className="hidden lg:block relative mx-auto min-h-[700px] max-w-6xl px-10">
        <div className="relative z-10 flex flex-col items-start w-full max-w-[420px] pt-16">
          <div className="w-full text-left">
            <h2 className="m-0 font-bold font-display text-5xl text-lp-text leading-tight tracking-wide uppercase">
              LIVING BOOKS
            </h2>
          </div>
          <div className="relative w-full h-[320px] my-24">
            <div className="absolute -left-[100vw] -top-10 z-0 h-[400px] w-[calc(100vw+550px)] rounded-r-full bg-lp-green-soft" />
            <div className="absolute -top-10 left-0 z-10 w-full h-[400px] flex flex-col items-center justify-center space-y-4 px-6">
              <Skeleton className="h-8 w-3/4 max-w-[280px] rounded-md" />
              <Skeleton className="h-5 w-1/2 max-w-[180px] rounded-md" />
              <Skeleton className="h-3 w-full max-w-[360px] rounded-md mt-4" />
              <Skeleton className="h-3 w-5/6 max-w-[300px] rounded-md" />
              <Skeleton className="h-3 w-2/3 max-w-[240px] rounded-md" />
            </div>
          </div>
          <div className="relative z-20 flex justify-center w-full">
            <Skeleton className="h-[56px] w-[180px] rounded-full" />
          </div>
        </div>
      
       <div className="absolute right-0 top-0 z-20 h-[700px] w-[700px]">
          <Skeleton className="absolute rounded-md" style={{ ...DESKTOP_SLOTS.main }} />
          <Skeleton className="absolute rounded-md opacity-50" style={{ ...DESKTOP_SLOTS.top }} />
          <Skeleton className="absolute rounded-md opacity-50" style={{ ...DESKTOP_SLOTS.bottom }} />
        </div>
      </div>

        <div className="lg:hidden flex flex-col items-center w-full">
          <div className="w-full max-w-5xl px-6 mb-4 flex justify-center text-center">
            <h2 className="m-0 font-bold font-display text-3xl text-[#6F6E6E] leading-tight uppercase">
              LIVING BOOKS
            </h2>
          <div className="mt-8 flex justify-center">
            <Skeleton
              className="rounded-md"
              style={{ width: 220, height: 310 }}
            />
          </div>
          <div className="relative w-full h-[400px] max-w-[320px] mx-auto z-20 mt-4">
            <Skeleton className="absolute rounded-md" style={{ ...MOBILE_SLOTS.main }} />
            <Skeleton className="absolute rounded-md opacity-50" style={{ ...MOBILE_SLOTS.top }} />
            <Skeleton className="absolute rounded-md opacity-50" style={{ ...MOBILE_SLOTS.bottom }} />
          </div>
      </div>
        
        <div className="w-full max-w-sm mx-auto mt-6 px-6">
          <div className="relative h-[180px] w-full flex flex-col items-center justify-center gap-3">
            <Skeleton className="h-7 w-3/4 max-w-[280px]" />
            <Skeleton className="h-5 w-1/2 max-w-[200px]" />
            <Skeleton className="h-3 w-full max-w-[300px]" />
            <Skeleton className="h-3 w-5/6 max-w-[260px]" />
          </div>
          <div className="flex justify-center mt-2">
            <Skeleton className="h-10 w-32 rounded-full" />
          </div>
        </div>

      </div>
    </section>
  );
}
